import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Fragment, useMemo, useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { RetirementInfoStep, RetireOnlineOptionsStep } from '.';
import Button from '../../../components/Button';
import Stepper from '../../../components/Stepper';
import { BeneficiariesProvider } from '../../../providers/beneficiaries';
import SelectBeneficiary from './reitreOnlineStep4/selectBeneficiary';
import RetireOnlineDirectDepositStep from './RetireOnlineDirectDepositStep';
import PlanSelectionStep, {
  optionInitialState,
} from './RetireOnlineStep3/PlanSelectionStep';
import LumpSumDistribution from './retireOnlineStep5/LumpSumDistribution';
import RetireOnlineStep7 from './RetireOnlineStep7/RetireOnlineStep7';
import ReviewAndConfirmationStep from './RetireOnlineStep8/ReviewAndConfirmationStep';
import RetireOnlineSummary from './RetireOnlineSummary';
import { stepScrollToTop } from '../../../utils/utils';

const retirementInfoStepData = {
  retirementDate: undefined,
  benefitsStartDate: undefined,
  electionMethod: undefined,
  martialStatus: undefined,
  martialData: {
    firstName: undefined,
    middleName: undefined,
    lastName: undefined,
    birthDate: undefined,
  },
};

const retirementOnlineOptionsStepData = {
  continueToPayOption: undefined,
  largeBenefitOption: undefined,
  SSOption: undefined,
  lumpSumOption: undefined,
};

const selectBeneficiaryStepData = {
  beneficiary: {
    id: undefined,
    name: undefined,
    relationship: undefined,
    image: undefined,
    beneficiaryValueInput: undefined,
  },
  contingents: [],
};

const retirementOnlinerollverPayment = {
  rolloverPaymentOption: undefined,
};

const directDepositStepData = {
  selectedBankOption: undefined,
  banksOptions: [
    { id: '1', name: 'Wells Fargo Bank', iban: '1234123412' },
    { id: '2', name: 'Bank of America', iban: '1234123412' },
  ],
};
const selectPlanStepData = {
  contributorySelectedPlan: optionInitialState,
  nonContributorySelectedPlan: optionInitialState,
  cashBalanceSelectedPlan: optionInitialState,
};

const texWithholdingFederalTaxData = {
  federalTaxData: {
    step1: { filingStatus: undefined },
    step2: { taxablePay: undefined, annuallyPay: undefined },
    step3: {
      children: undefined,
      dependepts: undefined,
      additionalAmount: undefined,
      totalAmount: undefined,
    },
    step4: {
      otherIncome: undefined,
      deductions: undefined,
      extraWithholdings: undefined,
    },
  },
  stateTaxData: {
    taxOption: undefined,
    state: { value: undefined, label: undefined },
    martialStatus: { value: undefined, label: undefined },
    numberOfAllowances: undefined,
    additionalAmount: undefined,
  },
};

const reviewAndConfirmationData = {
  confirmationCheckBoxChecked: undefined,
};

export type RetireOnlineStepperProps = {
  isVisible: (isVisible: boolean) => void;
};

const RetireOnlineStepper = ({ isVisible }: RetireOnlineStepperProps) => {
  const [step, setStep] = useState(0);
  const [step1, setStep1] = useState(retirementInfoStepData);
  const [step2, setStep2] = useState(retirementOnlineOptionsStepData);
  const [step3, setStep3] = useState(selectPlanStepData);
  const [step4, setStep4] = useState(selectBeneficiaryStepData);
  const [step5, setStep5] = useState(retirementOnlinerollverPayment);
  const [step6, setStep6] = useState(directDepositStepData);
  const [step7, setStep7] = useState(texWithholdingFederalTaxData);
  const [step8, setStep8] = useState(reviewAndConfirmationData);

  const isStep1Valid = useMemo(() => {
    const { retirementDate, benefitsStartDate, electionMethod, martialStatus } =
      step1;

    const { firstName, lastName, birthDate } = step1.martialData;

    if (martialStatus === 2) {
      if (!firstName || !lastName || !birthDate) {
        return false;
      }
    }

    if (
      !retirementDate ||
      !benefitsStartDate ||
      !electionMethod ||
      !martialStatus
    ) {
      return false;
    }

    return true;
  }, [step1]);

  const isStep3Valid = useMemo(() => {
    const {
      contributorySelectedPlan,
      nonContributorySelectedPlan,
      cashBalanceSelectedPlan,
    } = step3;
    if (
      contributorySelectedPlan.id === '' ||
      nonContributorySelectedPlan.id === '' ||
      cashBalanceSelectedPlan.id === ''
    ) {
      return false;
    }

    return true;
  }, [step3]);

  const isStep4Valid = useMemo(() => {
    const { beneficiary, contingents } = step4;
    if (!beneficiary && !contingents) return false;

    return true;
  }, [step4]);

  const isStep5Valid = useMemo(() => {
    const { rolloverPaymentOption } = step5;

    if (!rolloverPaymentOption) return false;

    return true;
  }, [step5]);

  const isStep6Valid = useMemo(() => {
    const { selectedBankOption } = step6;
    if (!selectedBankOption) {
      return false;
    }

    return true;
  }, [step6]);

  const isStep8Valid = useMemo(() => {
    const { confirmationCheckBoxChecked } = step8;
    if (!confirmationCheckBoxChecked) {
      return false;
    }
    return true;
  }, [step8]);

  const handleStep1Data = (data: any) => {
    setStep1(data);
  };

  const handleStep2Data = (data: any) => {
    setStep2(data);
  };

  const handleStep3Data = (data: any) => {
    setStep3(data);
  };

  const handleStep4Data = (data: any) => {
    setStep4(data);
  };

  const handleStep5Data = (data: any) => {
    setStep5(data);
  };

  const handleStep6Data = (data: any) => {
    setStep6(data);
  };

  const handleStep7Data = (data: any) => {
    setStep7(data);
  };

  const handleStep8Data = (data: any) => {
    setStep8(data);
  };

  const handleEditPreviousStep = (step: number) => {
    setStep(step);
  };

  const steps = [
    {
      title: 'Retirement information',
      child: (
        <RetirementInfoStep data={step1} collectStepData={handleStep1Data} />
      ),
    },
    {
      title: 'Option Selection',
      child: (
        <RetireOnlineOptionsStep
          data={step2}
          collectStepData={handleStep2Data}
        />
      ),
    },
    {
      title: 'Plan Selection',
      child: (
        <PlanSelectionStep data={step3} collectStepData={handleStep3Data} />
      ),
    },
    {
      title: 'Beneficiary',
      child: (
        <SelectBeneficiary data={step4} collectStepData={handleStep4Data} />
      ),
    },
    {
      title: 'Lump Sum Distribution Type',
      child: (
        <LumpSumDistribution data={step5} collectStepData={handleStep5Data} />
      ),
    },
    {
      title: 'Direct Deposit',
      child: (
        <RetireOnlineDirectDepositStep
          data={step6}
          collectStepData={handleStep6Data}
        />
      ),
    },
    {
      title: 'Tax Withholding',
      child: (
        <RetireOnlineStep7 data={step7} collectStepData={handleStep7Data} />
      ),
    },
    {
      title: 'Confirmation',
      child: (
        <ReviewAndConfirmationStep
          data={step8}
          dataRetirementInfo={step1}
          dataSelectPlan={step3}
          dataBeneficiary={step4}
          dataLumpSumDistribution={step5}
          dataDirectDeposit={step6}
          collectStepData={handleStep8Data}
          editPreviousStep={handleEditPreviousStep}
        />
      ),
    },
  ];

  const handleStepForward = () => {
    setStep(step + 1);
    if (step === 7) {
      isVisible(true);
    }
  };

  const handleStepBackward = () => {
    setStep(step - 1);
  };

  const handleClose = () => {
    isVisible(false);
  };

  const isStepValid = () => {
    if (
      (step === 0 && !isStep1Valid) ||
      (step === 2 && !isStep3Valid) ||
      (step === 3 && !isStep4Valid) ||
      (step === 4 && !isStep5Valid) ||
      (step === 5 && !isStep6Valid) ||
      (step === 7 && !isStep8Valid)
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    stepScrollToTop();
  }, [step]);

  return (
    <BeneficiariesProvider>
      <Fragment>
        {step === steps.length ? (
          <RetireOnlineSummary />
        ) : (
          <>
            <Container className="mb-12 mx-0 px-0">
              <Stepper currentStepIndex={step} steps={steps} />
            </Container>
            <section className="d-flex flex-column flex-lg-row justify-content-center">
              <Button
                variant="primary-outline"
                leftIcon={light('arrow-left')}
                onClick={handleStepBackward}
                disabled={step < 2}
                className={`mx-lg-10 ${
                  step === 0 && 'd-none'
                } order-2 order-lg-1 mt-7 mt-lg-0`}
              >
                Previous
              </Button>
              <Button
                variant="primary-outline"
                onClick={handleClose}
                className={'mx-lg-10 order-3 order-lg-2 mt-7 mt-lg-0'}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={isStepValid()}
                onClick={handleStepForward}
                className={'mx-lg-10 order-1 order-lg-3'}
              >
                {step === steps.length - 1 ? 'Complete' : 'Continue'}
              </Button>
            </section>
          </>
        )}
      </Fragment>
    </BeneficiariesProvider>
  );
};

export default RetireOnlineStepper;
